import type { FC } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import Image from '@atlaskit/image';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/standard-button';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CheckIcon from '@atlaskit/icon/core/migration/check-mark--check';
import AutomationIcon from '@atlaskit/icon/core/automation';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChartTrendIcon from '@atlaskit/icon/core/chart-trend';

import { withIsSiteAdmin, type WithIsSiteAdminProps } from '@confluence/change-edition';
import { GradientCTAButton } from '@confluence/change-edition/entry-points/GradientCTAButton';
import { Attribution, TransparentErrorBoundary } from '@confluence/error-boundary';
import { useChoreographerAssets } from '@confluence/choreographer-services';

import leftIllustration from './images/Illustration.svg';
import analyticsIconLight from './images/analyticsIcon-light.svg';
import analyticsIconDark from './images/analyticsIcon-dark.svg';
import automationIconLight from './images/automationIcon-ptort-light.svg';
import automationIconDark from './images/automationIcon-ptort-dark.svg';
import teamCalendarsIconLight from './images/teamCalendarsIcon-light.svg';
import teamCalendarsIconDark from './images/teamCalendarsIcon-dark.svg';
import type { ListItemProps } from './ListItem';
import { ListItem } from './ListItem';
import { PremiumFeatureKey } from './useTrackPremiumFeatureUsage';
import { i18n as i18nPto } from './PremiumTrialOnboardingModal';

const i18n = defineMessages({
	modalBodyText: {
		id: 'premium-onboarding.modal.body.text.pto.rt',
		description: 'Body text for modal',
		defaultMessage:
			'Get started by exploring our {n, plural, one {# most popular feature} other {# most popular features}}.',
	},
	additionalFeaturesTitle: {
		id: 'premium-onboarding.title.additional.features',
		description: 'Title text for additional features',
		defaultMessage: 'Plus:',
	},
	atlassianAIAdditionalFeatureText: {
		id: 'premium-onboarding.text.additional.feature.atlassian.ai',
		description: 'Text for Atlassian AI feature',
		defaultMessage: 'Atlassian Intelligence (AI)',
	},
	whiteboardAdditionalFeatureText: {
		id: 'premium-onboarding.text.additional.feature.whiteboard',
		description: 'Text for whiteboard and unlimited storage feature',
		defaultMessage: 'Unlimited whiteboards and storage',
	},
	bulkContentToolsAdditionalFeatureText: {
		id: 'premium-onboarding.text.additional.feature.bulk.content.tools',
		description: 'Text for bulk content management tools feature',
		defaultMessage: 'Bulk content management tools',
	},
	advancePermissionsAdditionalFeatureText: {
		id: 'premium-onboarding.text.additional.feature.advance.permissions',
		description: 'Text for advanced permissions feature',
		defaultMessage: 'Advanced permissions',
	},
});

const imageContainerStyles = xcss({
	width: '145',
	minInlineSize: 'max-content',
});

const textAreaStyles = xcss({
	width: '567px',
});

const textAreaBodyStyles = xcss({
	paddingInline: 'space.500',
	paddingBottom: 'space.300',
});

const closeButtonStyles = xcss({
	paddingRight: 'space.050',
	paddingTop: 'space.200',
	textAlign: 'right',
	minInlineSize: 'max-content',
});

const listItemWrapperStyles = xcss({
	marginBlock: 'space.300',
});

const buttonWrapperStyles = xcss({
	width: '100%',
	paddingTop: 'space.300',
	paddingBottom: 'space.100',
	textAlign: 'right',
});

type ModalProps = {
	stop?: () => void;
} & WithIsSiteAdminProps;

const ModalComponent: FC<ModalProps> = ({ isSiteAdmin, stop }) => {
	const [isOpen, setIsOpen] = useState(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { Modal } = useChoreographerAssets();

	const sharedFeaturesMapper: Array<ListItemProps & { featureKey: PremiumFeatureKey }> = [
		{
			title: i18nPto.teamCalendarsTitle,
			bodyText: i18nPto.teamCalendarsBodyText,
			img: teamCalendarsIconLight,
			darkImg: teamCalendarsIconDark,
			icon: CalendarIcon,
			featureKey: PremiumFeatureKey.TEAM_CALENDAR,
		},
		{
			title: i18nPto.automationTitle,
			bodyText: i18nPto.automationBodyText,
			img: automationIconLight,
			darkImg: automationIconDark,
			icon: AutomationIcon,
			featureKey: PremiumFeatureKey.AUTOMATION,
		},
		{
			title: i18nPto.analyticsTitle,
			bodyText: i18nPto.analyticsBodyTextPTO2,
			img: analyticsIconLight,
			darkImg: analyticsIconDark,
			icon: ChartTrendIcon,
			featureKey: PremiumFeatureKey.ANALYTICS,
		},
	];

	const closeModal = useCallback(
		async (actionSubjectId: any) => {
			actionSubjectId &&
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId,
						source: 'premiumTrialOnboardingReverseTrialModal',
					},
				}).fire();
			setIsOpen(false);
			stop?.();
		},
		[createAnalyticsEvent, stop],
	);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'premiumTrialOnboardingReverseTrialModal',
				attributes: {
					isSiteAdmin,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isSiteAdmin]);

	const additionalFeaturesList = [
		i18n.atlassianAIAdditionalFeatureText,
		i18n.whiteboardAdditionalFeatureText,
		i18n.bulkContentToolsAdditionalFeatureText,
		i18n.advancePermissionsAdditionalFeatureText,
	];

	return (
		<TransparentErrorBoundary attribution={Attribution.EDITIONS}>
			<ModalTransition>
				{isOpen && (
					<Modal onClose={closeModal} autoFocus={false} width={712} shouldScrollInViewport>
						<Inline alignBlock="stretch">
							<Box xcss={imageContainerStyles}>
								<Image
									src={leftIllustration}
									srcDark={leftIllustration}
									css={{
										objectFit: 'cover',
										minHeight: '100%',
										width: '168px',
										height: '400px !important',
										top: '-20px',
										left: '-23px',
									}}
								/>
							</Box>
							<Stack xcss={textAreaStyles}>
								<Box xcss={closeButtonStyles}>
									<Button
										appearance="link"
										onClick={() => closeModal('premiumTrialOnboardingReverseTrialModalCloseButton')}
									>
										<CrossIcon
											spacing="spacious"
											label="Close Modal"
											color={token('color.text', 'Neutral1000')}
										/>
									</Button>
								</Box>
								<Box xcss={textAreaBodyStyles}>
									<Stack space="space.100">
										<Heading size="medium" as="h1">
											<FormattedMessage {...i18nPto.modalTitle} />
										</Heading>
										<Text size="small" color="color.text.subtlest">
											<FormattedMessage
												{...i18n.modalBodyText}
												values={{
													n: sharedFeaturesMapper.length,
												}}
											/>
										</Text>
									</Stack>
									<Box xcss={listItemWrapperStyles}>
										{sharedFeaturesMapper.map(
											(
												feature: ListItemProps & {
													featureKey: PremiumFeatureKey;
												},
											) => (
												<ListItem
													key={feature.featureKey}
													bodyText={feature.bodyText}
													darkImg={feature.darkImg}
													img={feature.img}
													icon={feature.icon}
													title={feature.title}
												/>
											),
										)}
									</Box>
									<Text>
										<FormattedMessage {...i18n.additionalFeaturesTitle} />
									</Text>
									{additionalFeaturesList.map((feature, i) => (
										<Inline alignBlock="center" space="space.100" key={`additional-feature-${i}`}>
											<CheckIcon LEGACY_size="small" label="" />
											<Text>
												<FormattedMessage {...feature} />
											</Text>
										</Inline>
									))}
									<Box xcss={buttonWrapperStyles}>
										<GradientCTAButton
											onClick={() =>
												closeModal('premiumTrialOnboardingReverseTrialModalExploreFeatures')
											}
										>
											<FormattedMessage {...i18nPto.buttonText} />
										</GradientCTAButton>
									</Box>
								</Box>
							</Stack>
						</Inline>
					</Modal>
				)}
			</ModalTransition>
		</TransparentErrorBoundary>
	);
};

export const PremiumTrialOnboardingReverseTrialModal = withIsSiteAdmin(ModalComponent);
